import React, { useState, useEffect } from 'react';
import { Form, Typography, Checkbox, Radio, Input, Button } from 'antd';
import { 
  SmartphoneDevice, 
  DatabaseExport, 
  Headset, 
  Settings, 
  Heart, 
  CheckCircle,
  Rocket,
  Timer,
  QuestionMark,
  ThumbsUp,
  Minus,
  Star,
  User,
  AtSign,
  Building,
  ShieldCheck,
  Refresh
} from 'iconoir-react';
import './KioskFeedbackForm.css';

const { Title, Text } = Typography;
const { TextArea } = Input;

// Interfaces
interface KioskFeedbackData {
  // Step 0: User Information
  name?: string;
  email?: string;
  company?: string;
  
  // Step 1: Topic Selection & Session Rating
  topics?: string[];
  otherTopicSpecify?: string;
  sessionRating?: number;
  sessionRatingComments?: string;
  
  // Step 2: Session Insights (using existing backend fields)
  mostValuable?: string; // Maps to backend's mostValuable field
  
  // Step 3: Discussion Interest
  interestedInDiscussions?: string;
  discussionTopics?: string;
  
  // Step 4: Project Planning & Additional Comments
  projectStatus?: string;
  additionalComments?: string;
}

interface KioskFeedbackFormProps {
  currentStep: number;
  initialData: KioskFeedbackData;
  onSubmit: (data: Partial<KioskFeedbackData>) => void;
  onBack: () => void;
  showBackButton: boolean;
  isSubmitting?: boolean;
  onReset?: () => void;
}

// Data Constants
const topicOptions = [
  {
    value: 'Mobile Solution',
    label: 'Mobile Solution',
    icon: <SmartphoneDevice width="1.7rem" height="1.7rem" strokeWidth={1} className="text-blue-500" />,
    description: 'AI-enabled mobile apps'
  },
  {
    value: 'MAS Migration',
    label: 'MAS Migration',
    icon: <DatabaseExport width="1.7rem" height="1.7rem" strokeWidth={1} className="text-green-500" />,
    description: 'IBM Maximo migration'
  },
  {
    value: 'Support Extension',
    label: 'Support Extension',
    icon: <Headset width="1.7rem" height="1.7rem" strokeWidth={1} className="text-purple-500" />,
    description: 'Extended support services'
  },
  {
    value: 'Other',
    label: 'Other',
    icon: <Settings width="1.7rem" height="1.7rem" strokeWidth={1} className="text-gray-500" />,
    description: 'Specify other topics'
  },
];

const sessionRatings = [
  { 
    value: 5, 
    stringValue: 'Excellent',
    label: 'Excellent', 
    emoji: '😄',
    icon: <Star width="1.7rem" height="1.7rem" strokeWidth={1} className="text-green-600" />, 
    color: 'text-green-600',
    className: 'rating-excellent'
  },
  { 
    value: 4, 
    stringValue: 'Good',
    label: 'Good', 
    emoji: '😃',
    icon: <ThumbsUp width="1.7rem" height="1.7rem" strokeWidth={1} className="text-blue-600" />, 
    color: 'text-blue-600',
    className: 'rating-good'
  },
  { 
    value: 3, 
    stringValue: 'Average',
    label: 'Average', 
    emoji: '😐',
    icon: <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-yellow-600" />, 
    color: 'text-yellow-600',
    className: 'rating-average'
  },
  { 
    value: 2, 
    stringValue: 'Below Average',
    label: 'Below Average', 
    emoji: '😕',
    icon: <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-orange-600" />, 
    color: 'text-orange-600',
    className: 'rating-below-average'
  },
  { 
    value: 1, 
    stringValue: 'Poor',
    label: 'Poor', 
    emoji: '😞',
    icon: <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-red-600" />, 
    color: 'text-red-600',
    className: 'rating-poor'
  },
];

// Removed interestLevels - no longer needed

// Removed timelineOptions - no longer needed
// Removed solutionStatusOptions - no longer needed
// Removed discussionOptions - no longer needed

// Step 0: User Information Component
const KioskUserInfoStep: React.FC<{
  initialData: KioskFeedbackData;
  onSubmit: (data: any) => void;
  onBack: () => void;
  showBackButton: boolean;
}> = ({ initialData, onSubmit, onBack, showBackButton }) => {
  const [form] = Form.useForm();
  const [name, setName] = useState<string>(initialData.name || '');
  const [email, setEmail] = useState<string>(initialData.email || '');
  const [company, setCompany] = useState<string>(initialData.company || '');

  const handleSubmit = () => {
    const submitData = {
      name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
    };
    onSubmit(submitData);
  };

  const isFormValid = () => {
    return name.trim().length > 0 && email.trim().length > 0 && 
           email.includes('@') && email.includes('.');
  };

  return (
    <div className="kiosk-feedback-container">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        requiredMark={false}
        className="kiosk-form-content"
      >
        <div className="kiosk-form-sections">

          {/* User Information Form */}
          <div className="kiosk-form-section">
            <div className="kiosk-user-info-grid">
              {/* Name Field */}
              <div className="kiosk-user-info-field">
                <Title level={4} className="kiosk-field-label">
                  <User width="1.7rem" height="1.7rem" strokeWidth={1} className="inline mr-2" />Full Name *
                </Title>
                <Form.Item name="name">
                  <Input
                    size="large"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={100}
                    className="kiosk-input"
                    style={{ fontSize: '1rem', padding: '8px 12px' }}
                  />
                </Form.Item>
              </div>

              {/* Email Field */}
              <div className="kiosk-user-info-field">
                <Title level={4} className="kiosk-field-label">
                  <AtSign width="1.7rem" height="1.7rem" strokeWidth={1} className="inline mr-2" />Email Address *
                </Title>
                <Form.Item name="email">
                  <Input
                    size="large"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={150}
                    className="kiosk-input"
                    style={{ fontSize: '1rem', padding: '8px 12px' }}
                  />
                </Form.Item>
              </div>

              {/* Company Field */}
              <div className="kiosk-user-info-field">
                <Title level={4} className="kiosk-field-label">
                  <Building width="1.7rem" height="1.7rem" strokeWidth={1} className="inline mr-2" />Company/Organization
                  <Text className="text-sm text-gray-500 font-normal ml-2">(Optional)</Text>
                </Title>
                <Form.Item name="company">
                  <Input
                    size="large"
                    placeholder="Enter your company or organization"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                    maxLength={100}
                    className="kiosk-input"
                    style={{ fontSize: '1rem', padding: '8px 12px' }}
                  />
                </Form.Item>
              </div>
            </div>

            {/* Privacy Notice */}
            <div className="kiosk-privacy-notice">
              <Text className="text-sm text-gray-600">
                <ShieldCheck width="1.7rem" height="1.7rem" strokeWidth={1} className="inline mr-2" />Your information is secure and will only be used to follow up on your feedback if requested.
              </Text>
            </div>
          </div>
        </div>

        <div className="kiosk-nav-footer">
          {showBackButton ? (
            <Button
              size="large"
              onClick={onBack}
              className="kiosk-nav-button"
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            className="kiosk-nav-button-primary"
          >
            Continue to Session Rating
          </Button>
        </div>
      </Form>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Step 1: Topic Selection & Follow-Up Component
const KioskTopicSelectionStep: React.FC<{
  initialData: KioskFeedbackData;
  onSubmit: (data: any) => void;
  onBack: () => void;
  showBackButton: boolean;
}> = ({ initialData, onSubmit, onBack, showBackButton }) => {
  const [form] = Form.useForm();
  const [selectedTopics, setSelectedTopics] = useState<string[]>(initialData.topics || []);
  const [otherTopic, setOtherTopic] = useState<string>(initialData.otherTopicSpecify || '');
  const [sessionRating, setSessionRating] = useState<number | undefined>(initialData.sessionRating);
  const [sessionRatingComments, setSessionRatingComments] = useState<string>(initialData.sessionRatingComments || '');

  const handleTopicChange = (checkedValues: string[]) => {
    setSelectedTopics(checkedValues);
    if (!checkedValues.includes('Other')) {
      setOtherTopic('');
      form.setFieldValue('otherTopicSpecify', '');
    }
  };


  const handleSubmit = () => {
    const submitData = {
      topics: selectedTopics,
      otherTopicSpecify: selectedTopics.includes('Other') ? otherTopic : undefined,
      sessionRating: sessionRating,
      sessionRatingComments: sessionRatingComments || undefined,
    };
    onSubmit(submitData);
  };

  const isFormValid = () => {
    const hasTopics = selectedTopics.length > 0 && (!selectedTopics.includes('Other') || otherTopic.trim());
    const hasRating = sessionRating !== undefined;
    return hasTopics && hasRating;
  };


  return (
    <div className="kiosk-feedback-container">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        requiredMark={false}
        className="kiosk-form-content"
      >
        <div className="kiosk-form-sections">
          {/* Topic Selection */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              Which session(s) did you attend during the Maximo event?
            </Title>

            <Form.Item name="topics">
              <Checkbox.Group
                value={selectedTopics}
                onChange={handleTopicChange}
                className="w-full"
              >
                <div className="kiosk-topic-horizontal-grid">
                  {topicOptions.map((option) => (
                    <div 
                      key={option.value}
                      className={`kiosk-topic-card ${selectedTopics.includes(option.value) ? 'selected' : ''}`}
                    >
                      <Checkbox 
                        value={option.value}
                        className="kiosk-topic-checkbox-horizontal"
                      >
                        <div className="kiosk-topic-content-horizontal">
                          <div className="kiosk-topic-icon-horizontal">{option.icon}</div>
                          <div className="kiosk-topic-text-horizontal">
                            <div className="kiosk-topic-label-horizontal">{option.label}</div>
                            <div className="kiosk-topic-description-horizontal">{option.description}</div>
                          </div>
                        </div>
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Checkbox.Group>
            </Form.Item>

            {selectedTopics.includes('Other') && (
              <div className="kiosk-other-input-section">
                <Form.Item name="otherTopicSpecify" className="kiosk-other-form-item">
                  <Input
                    size="large"
                    placeholder="Please specify the other topic..."
                    value={otherTopic}
                    onChange={(e) => setOtherTopic(e.target.value)}
                    maxLength={100}
                    showCount
                    className="kiosk-input"
                  />
                </Form.Item>
              </div>
            )}
          </div>

          {/* Session Rating */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              How would you rate the session(s) you attended in terms of relevance and quality of content?
            </Title>

            <Form.Item name="sessionRating">
              <Radio.Group 
                value={sessionRating}
                onChange={(e) => setSessionRating(e.target.value)}
                className="kiosk-rating-group"
                size="large"
              >
                <div className="kiosk-rating-options-inline">
                  {sessionRatings.map((rating) => (
                    <div
                      key={rating.value}
                      className={`kiosk-rating-option-inline ${sessionRating === rating.value ? `selected ${rating.className}` : ''}`}
                      onClick={() => setSessionRating(rating.value)}
                    >
                      <Radio 
                        value={rating.value}
                        className="kiosk-rating-radio-inline"
                      >
                        <div className="kiosk-rating-content-inline">
                          <span className="kiosk-rating-emoji-inline text-2xl">{rating.emoji}</span>
                          <span className="kiosk-rating-label-inline">{rating.label}</span>
                        </div>
                      </Radio>
                    </div>
                  ))}
                </div>
              </Radio.Group>
            </Form.Item>

            {sessionRating && (
              <div className="kiosk-other-input-section mt-4">
                <Text className="text-sm text-gray-600">
                  (Optional: Please share any comments on what worked well or what could be improved.)
                </Text>
                <Form.Item name="sessionRatingComments" className="kiosk-other-form-item mt-2">
                  <TextArea
                    rows={3}
                    placeholder="Share your feedback about the session..."
                    value={sessionRatingComments}
                    onChange={(e) => setSessionRatingComments(e.target.value)}
                    maxLength={300}
                    showCount
                    className="kiosk-textarea"
                    style={{ fontSize: '1rem' }}
                  />
                </Form.Item>
              </div>
            )}
          </div>

        </div>

        <div className="kiosk-nav-footer">
          {showBackButton ? (
            <Button
              size="large"
              onClick={onBack}
              className="kiosk-nav-button"
            >
              Back
            </Button>
          ) : (
            <div />
          )}

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            className="kiosk-nav-button-primary"
          >
            Continue to Session Insights
          </Button>
        </div>
      </Form>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Removed old KioskSessionRatingStep - merged with comments step

// Step 2: Session Insights & Discussion Interest Component
const KioskImplementationStep: React.FC<{
  initialData: KioskFeedbackData;
  onSubmit: (data: any) => void;
  onBack: () => void;
}> = ({ initialData, onSubmit, onBack }) => {
  const [form] = Form.useForm();
  const [mostValuable, setMostValuable] = useState<string>(initialData.mostValuable || '');

  const handleInsightsChange = (e: any) => {
    setMostValuable(e.target.value);
  };

  const handleSubmit = () => {
    const submitData = {
      mostValuable: mostValuable,
    };
    onSubmit(submitData);
  };

  const isFormValid = () => {
    return mostValuable.length > 0;
  };

  return (
    <div className="kiosk-feedback-container">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        requiredMark={false}
        className="kiosk-form-content"
      >
        <div className="kiosk-form-sections">
          {/* Session Insights */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              Did the session(s) provide useful insights or information relevant to your current work or interests?
            </Title>

            <Form.Item name="mostValuable">
              <Radio.Group 
                value={mostValuable}
                onChange={handleInsightsChange}
                className="w-full"
                size="large"
              >
                <div className="kiosk-contact-grid">
                  <Radio.Button 
                    value="Technical insights and knowledge gained"
                    className={`kiosk-contact-button ${mostValuable === 'Technical insights and knowledge gained' ? 'selected contact-yes' : ''}`}
                  >
                    <div className="kiosk-contact-content">
                      <div className="kiosk-contact-text">
                        <div className="kiosk-contact-label">
                          Yes
                        </div>
                        <div className="kiosk-contact-description">
                          Very useful insights
                        </div>
                      </div>
                      <div className="kiosk-contact-icon">
                        <CheckCircle width="1.7rem" height="1.7rem" strokeWidth={1} className="text-green-500" />
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button 
                    value="Networking opportunities and connections"
                    className={`kiosk-contact-button ${mostValuable === 'Networking opportunities and connections' ? 'selected' : ''}`}
                  >
                    <div className="kiosk-contact-content">
                      <div className="kiosk-contact-text">
                        <div className="kiosk-contact-label">
                          Somewhat
                        </div>
                        <div className="kiosk-contact-description">
                          Partially useful
                        </div>
                      </div>
                      <div className="kiosk-contact-icon">
                        <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-yellow-500" />
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button 
                    value="Business insights and ROI understanding"
                    className={`kiosk-contact-button ${mostValuable === 'Business insights and ROI understanding' ? 'selected contact-no' : ''}`}
                  >
                    <div className="kiosk-contact-content">
                      <div className="kiosk-contact-text">
                        <div className="kiosk-contact-label">
                          No
                        </div>
                        <div className="kiosk-contact-description">
                          Not relevant to me
                        </div>
                      </div>
                      <div className="kiosk-contact-icon">
                        <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-red-500" />
                      </div>
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </Form.Item>

          </div>

        </div>

        <div className="kiosk-nav-footer">
          <Button
            size="large"
            onClick={onBack}
            className="kiosk-nav-button"
          >
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            className="kiosk-nav-button-primary"
          >
            Continue to Discussion Interest
          </Button>
        </div>
      </Form>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Step 3: Discussion Interest Component
const KioskDiscussionStep: React.FC<{
  initialData: KioskFeedbackData;
  onSubmit: (data: any) => void;
  onBack: () => void;
}> = ({ initialData, onSubmit, onBack }) => {
  const [form] = Form.useForm();
  const [interestedInDiscussions, setInterestedInDiscussions] = useState<string>(initialData.interestedInDiscussions || '');
  const [discussionTopics, setDiscussionTopics] = useState<string>(initialData.discussionTopics || '');

  const handleInterestChange = (e: any) => {
    setInterestedInDiscussions(e.target.value);
  };

  const handleSubmit = () => {
    const submitData = {
      interestedInDiscussions: interestedInDiscussions,
      discussionTopics: interestedInDiscussions === 'Yes' ? discussionTopics : undefined,
    };
    onSubmit(submitData);
  };

  const isFormValid = () => {
    const hasInterestAnswer = interestedInDiscussions.length > 0;
    const hasTopicsIfYes = interestedInDiscussions !== 'Yes' || discussionTopics.trim().length > 0;
    return hasInterestAnswer && hasTopicsIfYes;
  };

  return (
    <div className="kiosk-feedback-container">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        requiredMark={false}
        className="kiosk-form-content"
      >
        <div className="kiosk-form-sections">
          {/* Interest in Further Discussions */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              Would you be interested in having a deeper discussion with our experts on any of the topics covered?
            </Title>

            <Form.Item name="interestedInDiscussions">
              <Radio.Group 
                value={interestedInDiscussions}
                onChange={handleInterestChange}
                className="w-full"
                size="large"
              >
                <div className="kiosk-contact-grid">
                  <Radio.Button 
                    value="Yes"
                    className={`kiosk-contact-button ${interestedInDiscussions === 'Yes' ? 'selected contact-yes' : ''}`}
                  >
                    <div className="kiosk-contact-content">
                      <div className="kiosk-contact-text">
                        <div className="kiosk-contact-label">
                          Yes
                        </div>
                        <div className="kiosk-contact-description">
                          I would like to discuss further
                        </div>
                      </div>
                      <div className="kiosk-contact-icon">
                        <CheckCircle width="1.7rem" height="1.7rem" strokeWidth={1} className="text-green-500" />
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button 
                    value="No"
                    className={`kiosk-contact-button ${interestedInDiscussions === 'No' ? 'selected contact-no' : ''}`}
                  >
                    <div className="kiosk-contact-content">
                      <div className="kiosk-contact-text">
                        <div className="kiosk-contact-label">
                          No
                        </div>
                        <div className="kiosk-contact-description">
                          No further discussion needed
                        </div>
                      </div>
                      <div className="kiosk-contact-icon">
                        <Minus width="1.7rem" height="1.7rem" strokeWidth={1} className="text-red-500" />
                      </div>
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </Form.Item>

            {interestedInDiscussions === 'Yes' && (
              <div className="kiosk-other-input-section mt-4">
                <Text className="text-sm text-gray-600">
                  (If yes, please specify the topic/s)
                </Text>
                <Form.Item name="discussionTopics" className="kiosk-other-form-item mt-2">
                  <Input
                    size="large"
                    placeholder="Please specify the topics you'd like to discuss..."
                    value={discussionTopics}
                    onChange={(e) => setDiscussionTopics(e.target.value)}
                    maxLength={200}
                    className="kiosk-input"
                    style={{ fontSize: '1rem', padding: '8px 12px' }}
                  />
                </Form.Item>
              </div>
            )}
          </div>
        </div>

        <div className="kiosk-nav-footer">
          <Button
            size="large"
            onClick={onBack}
            className="kiosk-nav-button"
          >
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            className="kiosk-nav-button-primary"
          >
            Continue to Project Planning
          </Button>
        </div>
      </Form>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Step 4: Additional Comments Component
const KioskCommentsStep: React.FC<{
  initialData: KioskFeedbackData;
  onSubmit: (data: any) => void;
  onBack: () => void;
  isSubmitting: boolean;
}> = ({ initialData, onSubmit, onBack, isSubmitting }) => {
  const [form] = Form.useForm();
  const [projectStatus, setProjectStatus] = useState<string>(initialData.projectStatus || '');
  const [additionalComments, setAdditionalComments] = useState<string>(initialData.additionalComments || '');


  const handleProjectStatusChange = (e: any) => {
    setProjectStatus(e.target.value);
  };

  const handleCommentsChange = (e: any) => {
    setAdditionalComments(e.target.value);
  };

  const handleSubmit = () => {
    const submitData = {
      projectStatus: projectStatus,
      additionalComments: additionalComments || undefined,
    };
    onSubmit(submitData);
  };

  const isFormValid = () => {
    return projectStatus.length > 0; // Project status is required, comments optional
  };

  return (
    <div className="kiosk-feedback-container">
      <Form
        form={form}
        layout="vertical"
        initialValues={initialData}
        requiredMark={false}
        className="kiosk-form-content"
      >
        <div className="kiosk-form-sections">
          {/* Project Planning Status */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              Are you currently working on or planning a project related to the topic(s) you'd like to discuss further?
            </Title>
            
            <Form.Item name="projectStatus">
              <Radio.Group 
                value={projectStatus}
                onChange={handleProjectStatusChange}
                className="w-full"
                size="large"
              >
                <div className="kiosk-implementation-grid">
                  <Radio.Button 
                    value="Yes – currently implementing"
                    className={`kiosk-implementation-button ${projectStatus === 'Yes – currently implementing' ? 'selected impl-urgent' : ''}`}
                  >
                    <div className="kiosk-implementation-content">
                      <div className="kiosk-implementation-text">
                        <div className="kiosk-implementation-label">
                          Currently implementing
                        </div>
                        <div className="kiosk-implementation-description">
                          We have an active project
                        </div>
                      </div>
                      <div className="kiosk-implementation-icon">
                        <Rocket width="1.7rem" height="1.7rem" strokeWidth={1} className="text-orange-500" />
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button 
                    value="Yes – planning stage"
                    className={`kiosk-implementation-button ${projectStatus === 'Yes – planning stage' ? 'selected impl-short' : ''}`}
                  >
                    <div className="kiosk-implementation-content">
                      <div className="kiosk-implementation-text">
                        <div className="kiosk-implementation-label">
                          Planning stage
                        </div>
                        <div className="kiosk-implementation-description">
                          We're in planning phase
                        </div>
                      </div>
                      <div className="kiosk-implementation-icon">
                        <Timer width="1.7rem" height="1.7rem" strokeWidth={1} className="text-blue-500" />
                      </div>
                    </div>
                  </Radio.Button>
                  <Radio.Button 
                    value="No – just exploring"
                    className={`kiosk-implementation-button ${projectStatus === 'No – just exploring' ? 'selected impl-none' : ''}`}
                  >
                    <div className="kiosk-implementation-content">
                      <div className="kiosk-implementation-text">
                        <div className="kiosk-implementation-label">
                          Just exploring
                        </div>
                        <div className="kiosk-implementation-description">
                          No active project yet
                        </div>
                      </div>
                      <div className="kiosk-implementation-icon">
                        <QuestionMark width="1.7rem" height="1.7rem" strokeWidth={1} className="text-gray-500" />
                      </div>
                    </div>
                  </Radio.Button>
                </div>
              </Radio.Group>
            </Form.Item>
          </div>

          {/* Additional Comments */}
          <div className="kiosk-form-section">
            <Title level={4} className="kiosk-section-title">
              Any additional comments or questions?
              <Text className="text-base text-gray-500 font-normal ml-2">(Optional)</Text>
            </Title>

            <Form.Item name="additionalComments">
              <TextArea
                value={additionalComments}
                onChange={handleCommentsChange}
                placeholder="Share any additional thoughts, questions, or specific requirements..."
                rows={4}
                className="kiosk-textarea"
                maxLength={500}
                showCount
                style={{ fontSize: '1rem' }}
              />
            </Form.Item>
          </div>

        </div>

        <div className="kiosk-nav-footer">
          <Button
            size="large"
            onClick={onBack}
            disabled={isSubmitting}
            className="kiosk-nav-button"
          >
            Back
          </Button>

          <Button
            type="primary"
            size="large"
            onClick={handleSubmit}
            disabled={!isFormValid()}
            loading={isSubmitting}
            className="kiosk-nav-button-primary"
            style={{ minWidth: '200px' }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
          </Button>
        </div>
      </Form>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Step 5: Thank You Component
const KioskThankYouStep: React.FC<{
  onReset: () => void;
}> = ({ onReset }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onReset();
    }, 10000);

    return () => clearTimeout(timer);
  }, [onReset]);

  return (
    <div className="kiosk-thankyou-container">
      <div className="kiosk-thankyou-card">
        {/* Success Icon */}
        <div className="kiosk-thankyou-icon-container">
          <div className="kiosk-thankyou-icon-wrapper">
            <CheckCircle width="7.5rem" height="7.5rem" strokeWidth={1} className="text-green-500" />
            <div className="kiosk-thankyou-heart">
              <Heart width="1.7rem" height="1.7rem" strokeWidth={1} className="text-red-500" />
            </div>
          </div>
        </div>

        {/* Thank You Message */}
        <Title level={1} className="kiosk-thankyou-title">
          <CheckCircle width="2rem" height="2rem" strokeWidth={1} className="inline mr-2 text-green-500" />
          Thank You!
        </Title>
        
        <Title level={3} className="kiosk-thankyou-subtitle">
          Your feedback has been submitted successfully
        </Title>

        <Text className="kiosk-thankyou-message">
          Your input helps us serve you better and improve our services.
          <br />
          We truly appreciate the time you took to share your thoughts with us.
        </Text>

        {/* Auto-reset countdown */}
        <div className="kiosk-thankyou-countdown">
          <Text className="kiosk-thankyou-countdown-text">
            <Refresh width="1.5rem" height="1.5rem" strokeWidth={1} className="inline mr-2 text-blue-500" />
            This screen will reset automatically in a few seconds for the next user
          </Text>
        </div>

        {/* Manual reset button */}
        <Button
          type="primary"
          size="large"
          onClick={onReset}
          className="kiosk-thankyou-reset-button"
        >
          Start New Feedback Session
        </Button>

        {/* Contact info */}
        <div className="kiosk-thankyou-contact">
          <Text className="kiosk-thankyou-contact-text">
            For immediate assistance or questions, please contact our team
          </Text>
        </div>
      </div>
      
      {/* Powered by ThiranTech Footer */}
      <div className="kiosk-footer">
        <p className="kiosk-powered-by">
          Powered by <span className="kiosk-brand-highlight">ThiranTech</span>
        </p>
      </div>
    </div>
  );
};

// Main Consolidated Component
const KioskFeedbackForm: React.FC<KioskFeedbackFormProps> = ({
  currentStep,
  initialData,
  onSubmit,
  onBack,
  showBackButton,
  isSubmitting = false,
  onReset
}) => {
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <KioskUserInfoStep
            initialData={initialData}
            onSubmit={onSubmit}
            onBack={onBack}
            showBackButton={showBackButton}
          />
        );
      case 1:
        return (
          <KioskTopicSelectionStep
            initialData={initialData}
            onSubmit={onSubmit}
            onBack={onBack}
            showBackButton={showBackButton}
          />
        );
      case 2:
        return (
          <KioskImplementationStep
            initialData={initialData}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        );
      case 3:
        return (
          <KioskDiscussionStep
            initialData={initialData}
            onSubmit={onSubmit}
            onBack={onBack}
          />
        );
      case 4:
        return (
          <KioskCommentsStep
            initialData={initialData}
            onSubmit={onSubmit}
            onBack={onBack}
            isSubmitting={isSubmitting}
          />
        );
      case 5:
        return (
          <KioskThankYouStep
            onReset={onReset || (() => {})}
          />
        );
      default:
        return null;
    }
  };

  return renderStepContent();
};

export default KioskFeedbackForm;